import React, { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { gql, useMutation } from "@apollo/client";
import StarekContext from "../../context/StarekContext";

const AREA_OPTIONS = ["Sales", "Product Information", "Tech Support"];

const PRIORITY_OPTIONS = [
    { value: "Critical", label: "Critical — completely blocked" },
    { value: "High", label: "High — affecting business operations" },
    { value: "Medium", label: "Medium — can continue, workaround exists" },
    { value: "Low", label: "Low — enhancement or suggestion" },
];

const SUBMIT_SUPPORT_REQUEST = gql`
    mutation SubmitSupportRequest($data: SupportRequestInput!) {
        submitSupportRequest(data: $data)
    }
`;

function buildAccountName(lastUsedWorkspace, lastUsedPlant) {
    const parts = [lastUsedWorkspace?.name, lastUsedPlant?.name].filter(
        Boolean,
    );
    return parts.join(" — ");
}

function SupportModal({ open, onClose }) {
    const starekContext = useContext(StarekContext);
    const location = useLocation();
    const [accountName, setAccountName] = useState("");
    const [email, setEmail] = useState("");
    const [area, setArea] = useState("");
    const [priority, setPriority] = useState("");
    const [query, setQuery] = useState("");
    const [submitSupportRequest, { loading }] = useMutation(
        SUBMIT_SUPPORT_REQUEST,
        { client: starekContext.engineClient },
    );

    useEffect(() => {
        if (!open) return;
        setAccountName(
            buildAccountName(
                starekContext?.lastUsedWorkspace,
                starekContext?.lastUsedPlant,
            ),
        );
        setEmail(starekContext?.user?.email || "");
    }, [
        open,
        starekContext?.lastUsedWorkspace,
        starekContext?.lastUsedPlant,
        starekContext?.user?.email,
    ]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape" && !loading) onClose?.();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose, loading]);

    const handleClose = () => {
        if (loading) return;
        onClose?.();
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!email.trim() || !area || !priority || !query.trim()) {
            starekContext.showError(
                "Please fill in your email, an area, a priority, and your query before submitting.",
            );
            return;
        }

        const sourceUrl = `${window.location.origin}${location.pathname}${location.search}`;

        try {
            await submitSupportRequest({
                variables: {
                    data: {
                        account_name: accountName.trim(),
                        email: email.trim(),
                        area,
                        priority,
                        query: query.trim(),
                        source_url: sourceUrl,
                    },
                },
            });
            starekContext.showSuccess(
                "Your support request has been sent. Our team will get back to you shortly.",
            );
            setQuery("");
            setArea("");
            setPriority("");
            onClose?.();
        } catch (err) {
            starekContext.showError(err?.message || err);
        }
    };

    if (!open) {
        return null;
    }

    return (
        <div
            className="modal fade show"
            style={{ display: "block", backgroundColor: "rgba(0, 0, 0, 0.5)" }}
            onClick={(e) => {
                if (e.target === e.currentTarget) handleClose();
            }}
        >
            <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header bg-primary">
                        <h5 className="modal-title text-white fw-bold">
                            Contact Support
                        </h5>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            onClick={handleClose}
                            disabled={loading}
                            aria-label="Close"
                        ></button>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                            <p className="text-muted">
                                Send us a message and one of our team members
                                will get back to you shortly.
                            </p>

                            <div className="support-form-row">
                                <div className="form-group">
                                    <label htmlFor="support-account">
                                        Account
                                    </label>
                                    <input
                                        type="text"
                                        id="support-account"
                                        placeholder="Workspace / Plant"
                                        className="form-control"
                                        value={accountName}
                                        onChange={(e) =>
                                            setAccountName(e.target.value)
                                        }
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="support-email">
                                        Email
                                    </label>
                                    <input
                                        type="email"
                                        id="support-email"
                                        placeholder="you@example.com"
                                        className="form-control"
                                        value={email}
                                        onChange={(e) =>
                                            setEmail(e.target.value)
                                        }
                                    />
                                </div>
                            </div>

                            <div className="support-form-row">
                                <div className="form-group">
                                    <label htmlFor="support-area">Area</label>
                                    <select
                                        id="support-area"
                                        className="form-control"
                                        value={area}
                                        onChange={(e) =>
                                            setArea(e.target.value)
                                        }
                                    >
                                        <option value="">
                                            Select an area
                                        </option>
                                        {AREA_OPTIONS.map((option) => (
                                            <option
                                                key={option}
                                                value={option}
                                            >
                                                {option}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="support-priority">
                                        Priority
                                    </label>
                                    <select
                                        id="support-priority"
                                        className="form-control"
                                        value={priority}
                                        onChange={(e) =>
                                            setPriority(e.target.value)
                                        }
                                    >
                                        <option value="">
                                            Select a priority
                                        </option>
                                        {PRIORITY_OPTIONS.map((option) => (
                                            <option
                                                key={option.value}
                                                value={option.value}
                                            >
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="support-query">
                                    How can we help?
                                </label>
                                <textarea
                                    id="support-query"
                                    placeholder="Tell us what's going on..."
                                    rows={5}
                                    className="form-control"
                                    value={query}
                                    onChange={(e) =>
                                        setQuery(e.target.value)
                                    }
                                />
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={handleClose}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={loading}
                            >
                                {loading && (
                                    <span
                                        className="spinner-border spinner-border-sm me-2"
                                        role="status"
                                        aria-hidden="true"
                                    ></span>
                                )}
                                {loading ? (
                                    "Sending..."
                                ) : (
                                    <>
                                        <i className="fa fa-paper-plane mr-2"></i>
                                        Send Request
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default SupportModal;
